import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from 'recharts';
import { Loader2, TrendingDown, FileX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { reportsApi, getErrorMessage } from '../../services/api';
import { toast } from 'sonner';

// Stable colour palette per reason key. Using sharp accents (not gradients) so
// stacks read clearly even at small sizes — matches the rest of the dashboard.
const REASON_COLORS = {
  counter_offer: '#dc2626',           // red-600
  better_offer: '#ea580c',            // orange-600
  compensation_mismatch: '#d97706',   // amber-600
  location_mismatch: '#0891b2',       // cyan-600
  personal_reasons: '#7c3aed',        // violet-600
  lost_contact: '#64748b',            // slate-500
  other: '#0f766e',                   // teal-700
  unspecified: '#94a3b8',             // slate-400
};

const fallbackColor = (idx) => {
  const palette = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#a855f7', '#06b6d4', '#84cc16'];
  return palette[idx % palette.length];
};

const DropReasonsTrend = () => {
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState(null);
  const [months, setMonths] = React.useState(12);

  React.useEffect(() => {
    let cancel = false;
    setLoading(true);
    reportsApi
      .dropReasonsTrend(months)
      .then((res) => {
        if (!cancel) setData(res.data || null);
      })
      .catch((err) => {
        if (!cancel) {
          toast.error(getErrorMessage(err, 'Could not load drop reasons trend'));
          setData(null);
        }
      })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [months]);

  const isEmpty = !loading && (!data || data.total === 0);
  const reasons = data?.reasons || [];

  return (
    <Card data-testid="reports-drop-reasons-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-rose-600" />
            Drop Reasons (monthly)
            {data?.total ? (
              <Badge variant="outline" className="text-[10px] ml-1">
                {data.total} drops · last {months}m
              </Badge>
            ) : null}
          </CardTitle>
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden bg-white">
            {[6, 12, 24].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMonths(m)}
                className={`px-2.5 py-1 text-xs font-medium transition-colors ${
                  months === m ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
                data-testid={`drop-trend-window-${m}`}
              >
                {m}m
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
          </div>
        ) : isEmpty ? (
          <div className="text-center py-10 text-slate-400 text-sm flex flex-col items-center gap-2" data-testid="drop-trend-empty">
            <FileX className="w-8 h-8 text-slate-300" />
            No drops captured yet. As recruiters mark candidates "Dropped" with a reason, this chart will populate.
          </div>
        ) : (
          <>
            <div style={{ width: '100%', height: 280 }} data-testid="drop-trend-chart">
              <ResponsiveContainer>
                <BarChart data={data?.buckets || []} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#64748b" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#64748b" allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(value, name) => {
                      const r = reasons.find((x) => x.key === name);
                      return [value, r?.label || name];
                    }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: 11 }}
                    formatter={(value) => {
                      const r = reasons.find((x) => x.key === value);
                      return r?.label || value;
                    }}
                  />
                  {reasons.map((r, idx) => (
                    <Bar
                      key={r.key}
                      dataKey={r.key}
                      stackId="reasons"
                      fill={REASON_COLORS[r.key] || fallbackColor(idx)}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
            {reasons.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                {reasons.map((r) => (
                  <span
                    key={r.key}
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded border border-slate-200 bg-slate-50"
                    data-testid={`drop-trend-legend-${r.key}`}
                  >
                    <span
                      className="w-2 h-2 rounded-sm"
                      style={{ backgroundColor: REASON_COLORS[r.key] || fallbackColor(0) }}
                    />
                    {r.label}
                    <span className="text-slate-400">·</span>
                    <span className="font-semibold">{r.total}</span>
                  </span>
                ))}
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DropReasonsTrend;
